import { useEffect, useRef, useState } from 'react';

/**
 * Returns a number that changes each time the page containing `anchorRef` is hidden (Ionic hides
 * the page you leave with the `ion-page-hidden` class). Use it as the `key` of a page's scrolling
 * element: the element is rebuilt while the page is hidden, so it is back at the top, with
 * nothing scrolled, when the user returns.
 *
 * Why not just set scrollTop to 0: a hidden page is `display: none`, where setting a scroll
 * position does nothing, and Ionic's `ionViewWillLeave` / `ionViewDidLeave` hooks did not fire
 * for these tab pages. Remounting needs neither.
 *
 * `shouldSkip` keeps the position for some departures (e.g. opening an item from a list).
 */
export function useRemountOnPageHidden(
  anchorRef: { current: HTMLElement | null },
  shouldSkip?: () => boolean
): number {
  const [key, setKey] = useState(0);
  const skipRef = useRef(shouldSkip);
  useEffect(() => {
    skipRef.current = shouldSkip;
  });

  useEffect(() => {
    const page = anchorRef.current?.closest('.ion-page');
    if (!page) return;
    let hidden = page.classList.contains('ion-page-hidden');
    const observer = new MutationObserver(() => {
      const nowHidden = page.classList.contains('ion-page-hidden');
      if (nowHidden === hidden) return;
      hidden = nowHidden;
      if (nowHidden && !skipRef.current?.()) setKey(k => k + 1);
    });
    observer.observe(page, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
    // anchorRef is a ref object: stable
  }, [anchorRef]);

  return key;
}
